/**
 * Hook único para gerenciar o diálogo de fornecedor
 * Consolida toda a lógica de busca, seleção e sincronização
 */

"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDebounce } from "hooks";
import { apiClient, get, URL } from "api";
import toast from "react-hot-toast";
import {
  type FornecedorComboboxItem,
  type FornecedorRaw,
  type FornecedorResponse,
  formatarDataUltimaCompra,
  formatarTelefone,
  mapFornecedorToComboItem,
} from "@apiInclui/fornecedor";
import { usePrenotaStore } from "@PN/inclusao";
import {
  createComboItem,
  findExactCnpjMatch,
  isExactMatch,
  sortOptionsWithSelectedFirst,
} from "./utils";

// -----------------------------------------------------------------------------
// Interface do hook
// -----------------------------------------------------------------------------
export interface UseFornecedorDialog {
  /* state */
  open: boolean;
  term: string;
  isLoading: boolean;
  hasUserTyped: boolean;
  searchError: Error | null;
  options: FornecedorComboboxItem[];
  buttonLabel: string;
  selectedItem: FornecedorComboboxItem | null;

  /* computed */
  tooltipData: {
    nome: string;
    nomeReduzido?: string;
    cod: string;
    loja: string;
    cnpj?: string;
    telefone?: string;
    endereco?: string;
    municipio?: string;
    estado?: string;
    ultimaCompra?: string;
  } | null;

  /* actions */
  setOpen: (v: boolean) => void;
  handleInputChange: (v: string) => void;
  handleKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleSelectSupplier: (item: FornecedorComboboxItem) => Promise<void> | void;
  handleOpenChange: (v: boolean) => void;
  isItemSelected: (item: FornecedorComboboxItem) => boolean;
}

// -----------------------------------------------------------------------------
// Hook consolidado
// -----------------------------------------------------------------------------
export function useFornecedorDialog(): UseFornecedorDialog {
  // -------------------------------------- Estados do dialog
  const [open, setOpen] = useState(false);

  // -------------------------------------- Estados de busca
  const [term, setTerm] = useState("");
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const [searchData, setSearchData] = useState<FornecedorRaw[] | null>(null);
  const [searchError, setSearchError] = useState<Error | null>(null);

  // -------------------------------------- Estados de seleção
  const [selectedItem, setSelectedItem] =
    useState<FornecedorComboboxItem | null>(null);
  const [isAutoSearching, setIsAutoSearching] = useState(false);

  // -------------------------------------- Debounce
  const debouncedTerm = useDebounce(term, 400);

  // -------------------------------------- TanStack Query
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: async (filtro: string): Promise<FornecedorRaw[] | null> => {
      if (!filtro.trim()) return null;

      const response = await get<FornecedorResponse>(
        apiClient.internal,
        URL.in.API_PRENOTAS_FORNECEDORES,
        { headers: { filtro: filtro.trim() } }
      );

      if (!response.data?.success || !Array.isArray(response.data.data)) {
        throw new Error("API retornou dados inválidos");
      }

      return response.data.data ?? [];
    },
    onSuccess: (found) => {
      console.log(
        "🔍 [useFornecedorDialog] onSuccess - dados recebidos:",
        found
      );
      setSearchError(null);
      setSearchData(found);
    },
    onError: (err: Error) => {
      setSearchData(null);
      setSearchError(err);
      toast.error(err.message, { duration: 4000 });
    },
  });

  // -------------------------------------- Store
  const documento = usePrenotaStore((s) => s.draft.documento);
  const aux = usePrenotaStore((s) => s.aux);
  const updateDocumento = usePrenotaStore((s) => s.actions.draft.documento.set);
  const updateFornecedorAux = usePrenotaStore(
    (s) => s.actions.aux.fornecedor.set
  );

  // -------------------------------------- Computed values
  const isLoading = isPending || isAutoSearching;

  const options: FornecedorComboboxItem[] = useMemo(() => {
    console.log("🔍 [useFornecedorDialog] searchData:", searchData);
    if (!searchData?.length) return [];

    const mappedOptions = searchData.map(mapFornecedorToComboItem);
    console.log("🔍 [useFornecedorDialog] mappedOptions:", mappedOptions);
    return sortOptionsWithSelectedFirst(mappedOptions, selectedItem);
  }, [searchData, selectedItem]);

  const buttonLabel = selectedItem
    ? `(${selectedItem.cod} - ${selectedItem.loja}) ${selectedItem.nome}`
    : "Selecionar fornecedor";

  const tooltipData = useMemo(() => {
    if (!selectedItem) return null;

    return {
      nome: selectedItem.nome,
      nomeReduzido: selectedItem.nomeReduzido,
      cod: selectedItem.cod,
      loja: selectedItem.loja,
      cnpj: selectedItem.cnpj,
      telefone: selectedItem.telefone
        ? formatarTelefone(selectedItem.original.A2_DDD, selectedItem.telefone)
        : undefined,
      endereco: selectedItem.endereco,
      municipio: selectedItem.municipio,
      estado: selectedItem.estado,
      ultimaCompra: selectedItem.ultimaCompra
        ? formatarDataUltimaCompra(selectedItem.ultimaCompra)
        : undefined,
    };
  }, [selectedItem]);

  // -------------------------------------- Função de busca
  const searchFornecedor = useCallback(
    (filtro: string) => {
      mutate(filtro);
    },
    [mutate]
  );

  // -------------------------------------- Função de limpeza
  const clearSearch = useCallback(() => {
    setTerm("");
    setHasUserTyped(false);
    setSearchData(null);
    setSearchError(null);
    queryClient.removeQueries({ queryKey: ["fornecedores"] });
  }, [queryClient]);

  // -------------------------------------- Handlers
  const handleInputChange = useCallback((value: string) => {
    setTerm(value);
    if (value.trim().length >= 2) {
      setHasUserTyped(true);
    }
  }, []);

  const handleSelectSupplier = useCallback(
    async (item: FornecedorComboboxItem) => {
      setSelectedItem(item);

      // Atualiza o documento
      updateDocumento({
        ...documento,
        fornecedor: item.original.A2_COD || "",
        loja: item.original.A2_LOJA || "",
      });

      // Atualiza o aux com os dados do fornecedor
      updateFornecedorAux({
        nome: item.nome,
        cnpj: item.cnpj || "",
      });

      setOpen(false);
      clearSearch();
    },
    [updateDocumento, updateFornecedorAux, documento, clearSearch]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter" || isLoading) return;

      // Tenta seleção rápida primeiro
      if (options[0] && isExactMatch(term, options[0])) {
        handleSelectSupplier(options[0]);
        return;
      }

      // Senão, executa busca
      const trimmed = term.trim();
      if (!trimmed) return;

      searchFornecedor(trimmed);
      setHasUserTyped(true);
    },
    [term, isLoading, options, handleSelectSupplier, searchFornecedor]
  );

  const handleOpenChange = useCallback(
    (v: boolean) => {
      setOpen(v);
      if (!v) {
        // Só limpa o termo se não há fornecedor selecionado
        if (!selectedItem && !documento.fornecedor) {
          clearSearch();
        }
      }
    },
    [selectedItem, documento.fornecedor, clearSearch]
  );

  const isItemSelected = useCallback(
    (item: FornecedorComboboxItem) => {
      return selectedItem?.cod === item.cod && selectedItem?.loja === item.loja;
    },
    [selectedItem]
  );

  // -------------------------------------- Auto-busca por debounce
  useEffect(() => {
    console.log("🔍 [useFornecedorDialog] debouncedTerm:", debouncedTerm);
    if (debouncedTerm.trim().length >= 2) {
      console.log(
        "🔍 [useFornecedorDialog] Executando busca para:",
        debouncedTerm.trim()
      );
      searchFornecedor(debouncedTerm.trim());
    }
  }, [debouncedTerm, searchFornecedor]);

  // -------------------------------------- Sincronização com store
  useEffect(() => {
    const { fornecedor, loja } = documento;
    const { nome, cnpj } = aux.fornecedor || {};

    // Se store foi resetada, limpa estado interno
    if (!fornecedor && !cnpj) {
      setSelectedItem(null);
      setIsAutoSearching(false);
      return;
    }

    // Se store foi preenchida e não há item selecionado, ou se o item selecionado não corresponde à store, atualiza
    if (fornecedor && loja && nome) {
      const shouldUpdate =
        !selectedItem ||
        selectedItem.cod !== fornecedor ||
        selectedItem.loja !== loja;

      if (shouldUpdate) {
        const comboItem = createComboItem({
          A2_COD: fornecedor,
          A2_LOJA: loja,
          A2_NOME: nome,
          A2_CGC: cnpj || "",
        } as any);
        setSelectedItem(comboItem);
      }
    }

    // Busca automática por CNPJ
    if (cnpj && cnpj.trim().length > 0 && !fornecedor && !isAutoSearching) {
      setIsAutoSearching(true);
      searchFornecedor(cnpj.trim());
    }
  }, [
    documento,
    documento.fornecedor,
    documento.loja,
    aux.fornecedor,
    aux.fornecedor?.nome,
    aux.fornecedor?.cnpj,
    selectedItem,
    searchFornecedor,
    isAutoSearching,
  ]);

  // -------------------------------------- Busca automática por CNPJ
  useEffect(() => {
    if (!searchData?.length || selectedItem || !aux.fornecedor?.cnpj) {
      setIsAutoSearching(false);
      return;
    }

    const cnpj = aux.fornecedor.cnpj.trim();
    if (!cnpj) {
      setIsAutoSearching(false);
      return;
    }

    // Procura por match exato de CNPJ
    const exactMatch = findExactCnpjMatch(searchData, cnpj);

    if (exactMatch) {
      const comboItem = createComboItem(exactMatch);
      setSelectedItem(comboItem);

      // Atualiza store
      updateDocumento({
        ...documento,
        fornecedor: comboItem.original.A2_COD || "",
        loja: comboItem.original.A2_LOJA || "",
      });

      updateFornecedorAux({
        nome: comboItem.nome,
        cnpj: comboItem.cnpj || "",
      });

      setIsAutoSearching(false);
    } else {
      setIsAutoSearching(false);
    }
  }, [
    searchData,
    selectedItem,
    aux.fornecedor?.cnpj,
    updateDocumento,
    updateFornecedorAux,
    documento,
  ]);

  // -------------------------------------- Primeira busca quando abrir dialog
  useEffect(() => {
    if (open && !hasUserTyped && selectedItem) {
      const searchTerm =
        selectedItem.cnpj || selectedItem.cod || selectedItem.nome;
      if (searchTerm) {
        searchFornecedor(searchTerm);
        setTerm(searchTerm);
      }
    }
  }, [open, hasUserTyped, selectedItem, searchFornecedor]);

  return {
    open,
    term,
    isLoading,
    hasUserTyped,
    searchError,
    options,
    buttonLabel,
    selectedItem,
    tooltipData,
    setOpen,
    handleInputChange,
    handleKeyDown,
    handleSelectSupplier,
    handleOpenChange,
    isItemSelected,
  };
}
